import { z } from "zod";

export const GrantedPermissions = z.record(z.string(), z.string().array());
export type GrantedPermissions = z.infer<typeof GrantedPermissions>;

export const Permissions = z.record(z.string(), z.record(z.string(), z.boolean()));
export type Permissions = z.infer<typeof Permissions>;

// section names are translated with "sections.{section}"
// resource names are translated with "permissions.{resource}"
// actions are translated with "permissions.actions.{resource}:{action}" or with "permissions.actions.{action}"
export const PERMISSION_SECTIONS = {
    products: {
        // foo: ["bar"],
        product: ["read", "edit", "edit_minimum_durability", "edit_stock_position", "delete", "manage_state"],
        operational_cost: ["read", "edit", "delete"],
        ingredient: ["read", "edit", "delete"],
        category: ["read", "edit", "delete"],
        package: ["read", "edit", "edit_stock_position", "delete"],
        ean: ["read"],
        temperature_control: ["edit"],
        prototype: ["read", "edit", "delete"],
        preproduct: ["read", "edit"],
    },
    production: {
        order: ["read", "edit", "delete", "import"],
        production_plan: ["read", "edit", "delete"],
        protocol: ["read", "edit", "delete"],
        stock_item: ["read"],
        mixing: ["read"],
        mix_completion: ["read"],
        pick_container: ["read", "edit", "delete"],
        pick_order: ["read"],
        order_group: ["read", "edit", "delete"],
        cart: ["read", "edit"],
        tablet: ["read", "edit", "delete"],
        picking_settings: ["edit"],
    },
    stats: {
        stats: [
            "day2day",
            "aggregated_orders",
            "product_sales",
            "sent_packages",
            "mixing_mixes",
            "mix_completion",
            "logging",
            "waiting_orders",
            "rewards",
            "ordered_mixes",
            "production_analysis",
            "picking_items",
        ],
    },
    expedition: {
        label: ["generate", "print_qr_label"],
        expedition_package: ["read", "delete"],
        expedition_user_action: ["form"],
        expedition_user: ["read", "edit", "delete"],
        expedition_label_type: ["read"],
        package_code: ["read"],
    },
    settings: {
        admin_user: ["read", "edit", "delete"],
        role: ["read", "edit", "delete"],
        system_settings: ["edit"],
        employee: ["read", "edit", "delete"],
        job_lock: ["read", "edit"],
        carrier_handover_estimate: ["edit"],
        delivery_estimate: ["read", "edit"],
        ai_gateway: ["read", "edit", "edit_tokens"],
    },
} as const;

type Values<T> = T extends readonly (infer U)[] ? U : never;

type Section = keyof typeof PERMISSION_SECTIONS;
type Resources<S extends Section> = (typeof PERMISSION_SECTIONS)[S];
export type Resource = {
    [S in Section]: {
        [R in keyof Resources<S>]: R;
    }[keyof Resources<S>];
}[Section];
export type Permission = {
    [S in Section]: {
        [R in keyof Resources<S>]: `${R & string}:${Values<Resources<S>[R]> & (string | number)}`;
    }[keyof Resources<S>];
}[Section];

export type ResourceActions<R extends Resource> = {
    [S in Section]: {
        [_R in keyof Resources<S>]: R extends _R ? Values<Resources<S>[R]> & (string | number) : never;
    }[keyof Resources<S>];
}[Section];

export type ResourceHavingAction<A extends string> = {
    [S in Section]: {
        [R in keyof Resources<S>]: A extends Values<Resources<S>[R]> ? R : never;
    }[keyof Resources<S>];
}[Section];

export function resourceActions<R extends Resource>(resource: R): ResourceActions<R> {
    return (
        Object.values(PERMISSION_SECTIONS).reduce((a, b) => ({ ...a, ...b })) as unknown as Record<string, string[]>
    )[resource] as ResourceActions<R>;
}
